/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm as useHookForm } from 'react-hook-form'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import useDebounce from '../../../../hooks/useDebounce'
import useNotification from '../../../../hooks/useNotification'
import useLoader from '../../../../hooks/useLoader'
import useForm from '../../../../hooks/useForm'
import { entities } from '../../../../constants/entities'
import { baseMovementFields } from '../../../../constants/forms/movementFields'
import {
  cleanPayload,
  setDefaultFieldValues,
  transformObjectValues,
} from '../../../../utils/objectUtils'
import { transformFiltersForUrl } from '../../../../utils/transformFiltersForUrl'
import useBaseService from '../../../../hooks/services/useBaseService'
import useSelectOptions from '../../../../hooks/useSelectOptions'
import MovementEnum from '../../../../enums/movement-enum'
import useRemoteSelectOptions from '../../../../hooks/useRemoteSelectOptions'
import useQueryWithLoader from '../../../../hooks/useQueryWithLoader'
import { useOrgan } from '../../../../hooks/useOrgan'
import { useSectionFields } from '../../../../hooks/useSectionFields'
import { OrderSituationEnum } from '../../../../enums/order-enum'
import { groupProductItemsByOrganizationAndAddress } from '../../../../utils/groupProductItemsByOrganizationAndAddress'
import { useAuth } from '../../../../context/AuthContext'
import {
  clearMovementDraft,
  getMovementDraftKey,
  getSessionOrganizationId,
  readMovementDraft,
  saveMovementDraft,
  wasMovementSessionExpired,
} from '../utils/movementDraftStorage'

dayjs.extend(customParseFormat)

const useCreateMovement = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const firstInputRef = useRef(null)
  const inputRefs = useRef({})
  const queryClient = useQueryClient()

  // * Usado para página de adicionar item ao movimento
  // eslint-disable-next-line no-unused-vars
  const { movementId: urlMovementId, osId: routeOsId } = useParams()
  const [searchParams] = useSearchParams()
  const urlOsId = routeOsId || searchParams.get('service_order_id')

  const { selectedOrgan } = useOrgan()
  const { user } = useAuth()

  // Rascunho local apenas na tela de criação. A tela de adicionar item a um
  // movimento existente tem outro ciclo de vida e não deve compartilhar isso.
  const isCreatePage = !urlMovementId
  const [sessionOrganizationId] = useState(getSessionOrganizationId)
  const draftOrganizationId = selectedOrgan?.id ?? sessionOrganizationId
  const draftKey = isCreatePage
    ? getMovementDraftKey(user?.id, draftOrganizationId)
    : null
  const [initialDraft] = useState(() => readMovementDraft(draftKey))

  const restoredDraft = useMemo(() => {
    if (!initialDraft || initialDraft.expired) return null

    if (
      initialDraft.userId != null &&
      String(initialDraft.userId) !== String(user?.id)
    ) {
      return null
    }

    if (
      initialDraft.organizationId != null &&
      String(initialDraft.organizationId) !== String(draftOrganizationId)
    ) {
      return null
    }

    // Se a OS veio fixa pela URL, o rascunho só vale se for da mesma OS.
    if (urlOsId && String(initialDraft.serviceOrderId) !== String(urlOsId)) {
      return null
    }

    return initialDraft
  }, [initialDraft, user?.id, draftOrganizationId, urlOsId])

  const reservedEquipments = useRef(
    new Set(restoredDraft?.reservedEquipments ?? [])
  )
  const pendingReservationsRef = useRef(new Map())
  const isPageUnloadingRef = useRef(false)
  // Começa com o rascunho restaurado para preservar as quantidades de
  // produtos enquanto a OS ainda está carregando.
  const latestDraftRef = useRef(restoredDraft)
  const persistDraftRef = useRef(() => {})
  const releaseAllRef = useRef(() => {})

  const [reservedConfirmState, setReservedConfirmState] = useState({
    open: false,
    equipmentNumber: null,
    customerAlias: null,
    isBlocked: false,
    blockedStatus: null,
  })
  const reservedConfirmResolverRef = useRef(null)

  const askReservedEquipmentConfirmation = (
    equipmentNumber,
    customerAlias,
    isBlocked = false,
    blockedStatus = null
  ) =>
    new Promise((resolve) => {
      reservedConfirmResolverRef.current = resolve
      setReservedConfirmState({
        open: true,
        equipmentNumber,
        customerAlias,
        isBlocked,
        blockedStatus,
      })
    })

  const resolveReservedEquipmentConfirmation = (confirmed) => {
    reservedConfirmResolverRef.current?.(confirmed)
    reservedConfirmResolverRef.current = null
    setReservedConfirmState({
      open: false,
      equipmentNumber: null,
      customerAlias: null,
      isBlocked: false,
      blockedStatus: null,
    })
  }

  const handleReservedConfirm = () =>
    resolveReservedEquipmentConfirmation(true)
  const handleReservedCancel = () =>
    resolveReservedEquipmentConfirmation(false)

  const { showNotification } = useNotification()
  const { showLoader, hideLoader } = useLoader()

  const {
    post: baseServicePost,
    get: baseServiceGet,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate)

  const { formData, handleChange, resetForm, setFormData } = useForm(() => {
    const defaultFormData = {
      ...setDefaultFieldValues(baseMovementFields),
      movement: {
        ...setDefaultFieldValues(baseMovementFields).movement,
        movement_date: new Date().toISOString().split('T')[0],
      },
      items: [],
      filters: {
        movement: {
          service_order_id: {
            status_id: [1, 2],
            deleted_at: false,
          },
        },
      },
    }

    if (!restoredDraft?.formData) return defaultFormData

    return {
      ...defaultFormData,
      ...restoredDraft.formData,
      movement: {
        ...defaultFormData.movement,
        ...(restoredDraft.formData.movement ?? {}),
      },
      items: Array.isArray(restoredDraft.formData.items)
        ? restoredDraft.formData.items
        : [],
      // Os filtros são configuração de tela, não parte do rascunho.
      filters: defaultFormData.filters,
    }
  })

  const { updateField: updateProps } = useSectionFields()

  // Na tela de adicionar itens a requisição e a data já vêm definidas pela
  // URL/movimento; bloqueia os campos desde o primeiro render para evitar
  // que o usuário tente alterá-los enquanto os dados carregam.
  const [movementFields, setMovementFields] = useState(() => {
    if (!urlOsId) return baseMovementFields

    return [
      {
        section: baseMovementFields[0].section,
        fields: updateProps({
          arrayDependente: [...baseMovementFields[0].fields],
          field_id: ['movement.service_order_id', 'movement.movement_date'],
          updates: { disabled: true },
        }),
      },
    ]
  })

  const [assets, setAssets] = useState(() => restoredDraft?.assets ?? {})
  const [inputs, setInputs] = useState(() => restoredDraft?.inputs ?? {})
  const [products, setProducts] = useState([])
  const [volumes, setVolumes] = useState(() => restoredDraft?.volumes ?? [])
  const restoredProductQuantitiesRef = useRef(
    restoredDraft?.productQuantities ?? null
  )
  const [viewTable, setViewTable] = useState({})
  const [fieldsData, setFieldsData] = useState({})
  const [viewMode, setViewMode] = useState('form')
  const [filterOptions, setFilterOptions] = useState({
    deadline: null,
    recipient_customer: null,
    orderId: null,
  })

  // Efeito para notificações de rota
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  const ordersServicesSelect = useRemoteSelectOptions({
    queryKey: ['serviceOrders', selectedOrgan?.id],
    baseServiceGet,
    entityPath: entities.serviceOrders.get,
    columnDataBase: [
      'id',
      'alias',
      'items.type.name',
      'contract.number',
      'recipient_customer.alias',
    ],
    extraFilters: {
      organization_id: selectedOrgan?.id,
      // Na tela de adicionar itens a requisição já vem fixa pela URL: busca
      // diretamente pelo ID, sem depender dos filtros de status/situação.
      ...(!urlOsId && {
        status_id: { $in: [1, 2] },
        situation: { $eq: OrderSituationEnum.APPROVED },
      }),
    },
    defaultSort: { column: 'id', order: 'desc' },
    fieldId: 'movement.service_order_id',
    formData,
    enabled: !!selectedOrgan?.id,
    mapToOption: (order) => ({
      value: order.id,
      label: `R #${order.id} - ${order?.movement_type?.name} - ${order.recipient_customer?.alias}`,
    }),
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    immediateFetch: !urlOsId,
    initialId: urlOsId ?? restoredDraft?.serviceOrderId ?? null,
    fetcherById: async (id) => {
      const response = await baseServiceGet(entities.serviceOrders.getById(id))
      return response.result
    },
  })

  const serviceOrderId =
    typeof formData.movement?.service_order_id === 'object'
      ? formData.movement?.service_order_id?.value
      : (formData.movement?.service_order_id ?? urlOsId)

  // ---------------------------------------------------------------------------
  // Rascunho local (localStorage)
  //
  // O objetivo é sobreviver a um reload sem liberar as reservas de
  // carregamento. A liberação só acontece em saída explícita (Voltar/menu) ou
  // no sucesso do cadastro.
  // ---------------------------------------------------------------------------

  const releaseEquipmentReservation = useCallback(
    async (equipmentId) => {
      if (equipmentId == null) return

      // Se o reserve-loading ainda está em voo, espera ele terminar. Sem isso,
      // a liberação pode chegar antes e o equipamento ficar preso em
      // AGUARDANDO CARREGAMENTO.
      const pendingReservation =
        pendingReservationsRef.current.get(equipmentId)
      if (pendingReservation) {
        await pendingReservation
      }

      try {
        await baseServicePost(
          `${entities.equipments.base}/${equipmentId}/release-loading`,
          {},
          { skipNotification: true }
        )
      } catch (error) {
        console.error('Erro ao liberar reserva do equipamento:', error)
      }
    },
    [baseServicePost]
  )

  const releaseEquipmentReservations = useCallback(
    async (ids) => {
      if (!Array.isArray(ids) || ids.length === 0) return

      await Promise.allSettled(
        ids.map((equipmentId) => releaseEquipmentReservation(equipmentId))
      )
    },
    [releaseEquipmentReservation]
  )

  const releaseAllReservations = useCallback(
    async ({ clearDraft = false } = {}) => {
      // Limpa o rascunho imediatamente para evitar que um retorno rápido à
      // tela restaure itens cujas reservas estão sendo liberadas agora.
      if (clearDraft && draftKey) {
        latestDraftRef.current = null
        clearMovementDraft(draftKey)
      }

      // Espera reservas em voo antes de liberar: sem isso, um release pode
      // chegar ao backend antes do reserve e o equipamento fica preso em
      // AGUARDANDO CARREGAMENTO.
      if (pendingReservationsRef.current.size > 0) {
        await Promise.allSettled(
          Array.from(pendingReservationsRef.current.values())
        )
      }

      const ids = Array.from(reservedEquipments.current)
      reservedEquipments.current.clear()

      if (ids.length > 0) {
        await releaseEquipmentReservations(ids)
      }
    },
    [draftKey, releaseEquipmentReservations]
  )

  releaseAllRef.current = releaseAllReservations

  // Rascunho expirado ou de outra OS: não pode ser retomado nesta tela.
  // Libera as reservas que ficaram órfãs (o endpoint é idempotente e ignora
  // equipamentos que já mudaram de status) e descarta o rascunho.
  useEffect(() => {
    if (!initialDraft) return
    if (!initialDraft.expired && restoredDraft) return

    releaseEquipmentReservations(initialDraft.reservedEquipments ?? [])
    clearMovementDraft(draftKey)
    latestDraftRef.current = null
  }, [])

  const buildDraftPayload = useCallback(
    () => ({
      userId: user?.id ?? null,
      organizationId: draftOrganizationId ?? null,
      serviceOrderId: serviceOrderId ?? null,
      formData: {
        movement: formData.movement ?? {},
        items: Array.isArray(formData.items) ? formData.items : [],
      },
      assets,
      inputs,
      volumes,
      reservedEquipments: Array.from(reservedEquipments.current),
      // Enquanto os produtos da OS ainda não carregaram, preserva as
      // quantidades do rascunho anterior para não zerar a alocação.
      productQuantities:
        products.length > 0
          ? products.reduce((acc, product) => {
              if (product?.identify) {
                acc[product.identify] = product.quantity
              }
              return acc
            }, {})
          : (latestDraftRef.current?.productQuantities ?? {}),
    }),
    [
      user?.id,
      draftOrganizationId,
      serviceOrderId,
      formData.movement,
      formData.items,
      assets,
      inputs,
      volumes,
      products,
    ]
  )

  const persistDraftNow = useCallback(() => {
    if (!draftKey) return

    const hasContent =
      Boolean(serviceOrderId) ||
      Object.keys(assets ?? {}).length > 0 ||
      Object.keys(inputs ?? {}).length > 0 ||
      (Array.isArray(volumes) && volumes.length > 0)

    if (!hasContent) {
      latestDraftRef.current = null
      clearMovementDraft(draftKey)
      return
    }

    const payload = buildDraftPayload()
    latestDraftRef.current = payload
    saveMovementDraft(draftKey, payload)
  }, [draftKey, serviceOrderId, assets, inputs, volumes, buildDraftPayload])

  persistDraftRef.current = persistDraftNow

  // Salva com um pequeno debounce a cada mudança relevante.
  useEffect(() => {
    if (!draftKey) return undefined

    const timer = setTimeout(() => {
      persistDraftRef.current?.()
    }, 400)

    return () => clearTimeout(timer)
  }, [
    draftKey,
    serviceOrderId,
    formData.movement,
    formData.items,
    assets,
    inputs,
    volumes,
    products,
  ])

  const {
    data: serviceOrderItemsData,
    refetch: refetchServiceOrderItems,
  } = useQueryWithLoader({
    queryKey: [
      'serviceOrderItems',
      serviceOrderId,
      user?.id,
      draftOrganizationId,
    ],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl({
        status_id: { $in: [1, 2] },
      })

      const params = {
        filters: filtersTransformed,
        page: 1,
        perPage: 999999999999,
      }
      const response = await baseServiceGet(
        entities.serviceOrders.items(serviceOrderId),
        params
      )
      // Rejected excess lines are not eligible for a movement even though
      // their general item status may still be active/approved.
      return response.result.data.filter(
        (item) =>
          !(
            Number(item.is_exchange) === 1 &&
            String(item.exchange_approval_status || '').toLowerCase() ===
              'rejected'
          )
      )
    },
    enabled: !!serviceOrderId,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })

  // Ao escolher uma requisição manualmente, força a carga dos itens para essa
  // OS. Isso também cobre o caso em que o formulário atualiza o valor do select
  // sem a query habilitada ser reavaliada no mesmo ciclo.
  useEffect(() => {
    if (serviceOrderId) {
      refetchServiceOrderItems()
    }
  }, [serviceOrderId, refetchServiceOrderItems])

  const serviceOrderSelected = ordersServicesSelect.rawData?.find(
    (order) => order.id === Number(serviceOrderId)
  )

  useEffect(() => {
    if (!serviceOrderItemsData) return

    const nextProducts = serviceOrderItemsData
      .filter(
        (item) =>
          item.product &&
          (item.fulfilled_quantity_product > 0 ||
            serviceOrderSelected?.movement_type?.id === MovementEnum.RESERVA)
      )
      .map((item) => ({
        product_id: item.product.id,
        service_order_id: item.service_order_id,
        product_items: groupProductItemsByOrganizationAndAddress(
          item.product.items.filter((pi) => pi.quantity > 0 && !pi.deleted_at)
        ),
        service_order_item: item,
        alias: item.product.name,
        quantity: item.fulfilled_quantity_product,
        requested_quantity: item.quantity_product,
        identify: `prod-${item.service_order_id}-${item.product.id}`,
        // usado para calcular a quantidade restante do produto (calculos diferentes nas paginas de criação e adição de itens)
        is_create_page: true,
      }))

    // Ao retomar um rascunho, reaplica as quantidades que já tinham sido
    // alocadas nos volumes (o backend devolve sempre a quantidade cheia).
    const restoredQuantities = restoredProductQuantitiesRef.current
    restoredProductQuantitiesRef.current = null

    if (!restoredQuantities) {
      setProducts(nextProducts)
      return
    }

    setProducts(
      nextProducts.map((product) => {
        const restoredQuantity = restoredQuantities[product.identify]
        return restoredQuantity == null
          ? product
          : { ...product, quantity: restoredQuantity }
      })
    )
  }, [serviceOrderItemsData, serviceOrderId, serviceOrderSelected])

  const contractId = serviceOrderSelected?.contract_id

  const hasEquipments =
    Array.isArray(serviceOrderItemsData) &&
    serviceOrderItemsData?.some((item) => item.equipment_id)

  const hasReplacement =
    serviceOrderSelected?.movement_type?.id === MovementEnum.SUBSTITUICAO

  const hasDevolution =
    serviceOrderSelected?.movement_type?.id === MovementEnum.DEVOLUCAO

  const hasExchanges =
    Array.isArray(serviceOrderItemsData) &&
    serviceOrderItemsData.some((item) => item.type !== null)

  const hasExtrasEquipments =
    Array.isArray(serviceOrderItemsData) &&
    serviceOrderItemsData.some(
      (item) =>
        item.extra_equipments !== null && item.extra_equipments.length > 0
    )

  const contractItemIds =
    Array.isArray(serviceOrderItemsData) &&
    serviceOrderItemsData
      ?.map((item) => item.contract_item_id)
      .filter((id) => id != null && !isNaN(Number(id)))
      .map((id) => Number(id))

  const hasContract = Boolean(
    ordersServicesSelect.rawData.find(
      (item) => item.id === ordersServicesSelect.selectedValue?.value
    )?.contract
  )

  // * Setar as informações quando estiver vindo da página de adicionar item ao movimento
  useEffect(() => {
    if (urlOsId && ordersServicesSelect.options.length > 0) {
      let updatedFields = [...baseMovementFields[0].fields]
      const selectedOrderOption =
        ordersServicesSelect.options.find(
          (option) => String(option.value) === String(urlOsId)
        ) ?? ordersServicesSelect.options[0]

      setFormData((prev) => ({
        ...prev,
        movement: {
          ...prev.movement,
          service_order_id: selectedOrderOption,
        },
      }))

      // * Desabilitar o campode de service_order_id e data
      updatedFields = updateProps({
        arrayDependente: updatedFields,
        field_id: ['movement.service_order_id', 'movement.movement_date'],
        updates: {
          disabled: true,
        },
      })

      setMovementFields([
        {
          section: baseMovementFields[0].section,
          fields: updatedFields,
        },
      ])
    }
  }, [urlOsId, ordersServicesSelect.options[0]])

  // Query para buscar e processar os itens de contrato
  const { data: equipmentsData, isLoading: isLoadingEquipments } =
    useQueryWithLoader({
      queryKey: [
        'contractItemsForMovement',
        serviceOrderId,
        contractId,
        contractItemIds,
        user?.id,
        draftOrganizationId,
      ],
      queryFn: async () => {
        showLoader()
        try {
          const filters = transformFiltersForUrl({
            deleted_at: false,
            ...(contractItemIds.length > 0 && { id: contractItemIds }),
          })
          const params = { filters, page: 1, perPage: 0 }

          // * Quando a requisição não tiver contrato
          if (!contractId) {
            const response = await baseServiceGet(
              entities.serviceOrders.items(serviceOrderId),
              params
            )
            return response.result.data
          }
          // * Quando a requisição tiver contrato
          else {
            const response = await baseServiceGet(
              entities.contracts.items(contractId),
              params
            )
            return response.result.data
          }
        } finally {
          hideLoader()
        }
      },
      enabled: hasContract ? !!contractId : !!serviceOrderId,
      staleTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
    })

  // TODO: Em desenvolvimento
  const equipmentDataValidated = useMemo(() => {
    if (!equipmentsData) return []
    const repeatedItems = []

    // !Caso de equipamentos (SUBSTITUICAO, DEVOLUÇÃO)
    if (hasEquipments && (hasReplacement || hasDevolution)) {
      serviceOrderItemsData.forEach((item) => {
        if (!item.equipment_id && !item.equipment) return
        // Repete com base na quantidade atendida (fulfilled_quantity)
        for (let i = 0; i < (item.fulfilled_quantity || 0); i++) {
          const baseEquipment = item.equipment || {}
          const equipmentId = baseEquipment.id || item.equipment_id
          const contractItemId = item.contract_item_id || equipmentId

          repeatedItems.push({
            ...baseEquipment,
            extra_id:
              item.equipment?.last_relevant_movement_item?.extra_id ?? null,
            contract_item: item.contract_item,
            is_extra: false,
            // Identify único para cada linha repetida do mesmo equipamento
            service_order_item_id: item.id,
            identify: `equip-${contractItemId}-${equipmentId}-${i}`,
            _identify: `equip-${contractItemId}-${equipmentId}`,
          })
        }
      })
    } else {
      // !Caso de equipamentos (ALUGUEL)
      if (hasExchanges) {
        serviceOrderItemsData.forEach((osItem) => {
          for (let i = 0; i < (osItem.fulfilled_quantity || 0); i++) {
            const exchangeTypeId = osItem.type?.id
            const identifyBase = `equip-${exchangeTypeId}-${osItem.id}`

            repeatedItems.push({
              id: osItem.id,
              alias: osItem.type.name, // Nome do tipo de permuta
              name: osItem.type.name, // Alias do equipamento
              type: osItem.type,
              service_order_item_id: osItem.id,
              movement_type_id: osItem.movement_type_id,
              is_extra: false,
              identify: `${identifyBase}-${i}`,
              _identify: identifyBase,
            })
          }
        })
      } else if (hasContract && !hasDevolution && !hasReplacement) {
        Object.values(equipmentsData).forEach((contractItem) => {
          const onlyContractItem = serviceOrderItemsData?.filter(
            (item) => item.contract_item_id
          )

          const osItem = onlyContractItem?.find(
            (osItem) =>
              String(osItem.contract_item_id) === String(contractItem.id) ||
              // ! Esse caso abaixo pega quando o contract_item_id mudou devido a algum evento no contrato
              String(osItem.contract_item_id) ===
                String(contractItem.root_item_id)
          )

                    if (!osItem) return

          // 🆕 CORREÇÃO PARA RESERVA:
          // Se for RESERVA (id 4), usa a quantidade total solicitada (quantity).
          // Caso contrário, mantém o comportamento original (fulfilled_quantity).
          const isReserva = serviceOrderSelected?.movement_type?.id === 4;
          const quantidadeParaRepetir = isReserva ? (osItem.quantity || 1) : (osItem.fulfilled_quantity || 0);

          // Repete o equipamento principal de acordo com a quantidade calculada
          for (let i = 0; i < quantidadeParaRepetir; i++) {
            const baseEquipment = contractItem.equipment || contractItem
            const equipmentId = baseEquipment.id || contractItem.id
            const contractItemId = contractItem.id

            repeatedItems.push({
              ...baseEquipment,
              contract_item: contractItem.contract_item || contractItem,
              extras: [], 
              is_extra: false,
              identify: `equip-${contractItemId}-${equipmentId}-${i}`,
              _identify: `equip-${contractItemId}-${equipmentId}`,
            })
          }

          // Agora adiciona os extras **uma única vez por pivot.quantity**, fora do loop do equipamento
          if (hasExtrasEquipments) {
            serviceOrderItemsData
              .filter(
                (item) =>
                  item.extra_equipments &&
                  item.contract_item_id === contractItem.id
              )
              .forEach((extraEquipment) => {
                extraEquipment.extra_equipments
                  ?.filter((extra) => !extra.pivot?.deleted_at)
                  ?.forEach((extra) => {
                    for (
                      let q = 0;
                      q < (extra?.pivot?.fulfilled_quantity || 0);
                      q++
                    ) {
                      repeatedItems.push({
                        ...extra,
                        extra_id: extra.pivot?.id,
                        contract_item:
                          contractItem.contract_item || contractItem,
                        description: null,
                        is_extra: true,
                        // Mantemos a referência ao item pai se necessário
                        parent_identify: `equip-${contractItem.id}-${extraEquipment.equipment?.id || extraEquipment.id}-0`,
                        identify: `equip-${contractItem.id}-extra-${extra.id}-${q}`,
                        _identify: `equip-${contractItem.id}-${extraEquipment.equipment?.id || extraEquipment.id}`,
                      })
                    }
                  })
              })
          }
        })
      }
    }

    return repeatedItems
  }, [
    equipmentsData,
    serviceOrderItemsData,
    hasEquipments,
    hasExchanges, // Permutas
    hasExtrasEquipments,
    hasReplacement,
    hasDevolution,
  ])

  const initialEquipmentsNumber = useMemo(() => {
    if (!equipmentDataValidated) {
      return {}
    }

    return equipmentDataValidated.reduce((acc, item) => {
      acc[item.identify] = ''
      return acc
    }, {})
  }, [equipmentDataValidated])

  const initialCode = useMemo(() => {
    if (!products) {
      return {}
    }
    return products.reduce((acc, item) => {
      acc[item.identify] = ''
      return acc
    }, {})
  }, [products])

  // Valores dos inputs (tombo/código) dos itens retomados do rascunho.
  const restoredEquipmentsNumber = useMemo(() => {
    if (!restoredDraft?.assets) return {}

    return Object.entries(restoredDraft.assets).reduce(
      (acc, [identify, asset]) => {
        const number = asset?.equipment?.number ?? asset?.number
        if (number !== undefined && number !== null) {
          acc[identify] = String(number)
        }
        return acc
      },
      {}
    )
  }, [restoredDraft])

  const restoredCodes = useMemo(() => {
    if (!restoredDraft?.inputs) return {}

    return Object.entries(restoredDraft.inputs).reduce(
      (acc, [identify, input]) => {
        if (input?.code !== undefined && input?.code !== null) {
          acc[identify] = String(input.code)
        }
        return acc
      },
      {}
    )
  }, [restoredDraft])

  const { control, setValue, reset } = useHookForm({
    defaultValues: {
      equipmentsNumber: {
        ...initialEquipmentsNumber,
        ...restoredEquipmentsNumber,
      },
      code: { ...initialCode, ...restoredCodes },
    },
  })

  const serviceOrderRef = useRef(serviceOrderId)

  useEffect(() => {
    if (serviceOrderRef.current !== serviceOrderId) {
      serviceOrderRef.current = serviceOrderId

      // Trocou de OS: as reservas e o rascunho pertencem à OS anterior.
      releaseAllRef.current?.({ clearDraft: true })
      restoredProductQuantitiesRef.current = null

      setAssets({})
      setInputs({})
      setVolumes([])
      setProducts([])
      reset({
        equipmentsNumber: initialEquipmentsNumber,
        code: initialCode,
      })
    }
  }, [serviceOrderId, setValue, initialEquipmentsNumber, initialCode])

  const selectedOrderData = ordersServicesSelect.rawData?.find(
    (order) => String(order.id) === String(serviceOrderId)
  )

  // Buscar os clientes destinatários do contrato
  const recipientCustomerSelect = useSelectOptions({
    fieldId: 'movement.recipient_customer_id',
    formData,
    isMulti: false,
    rawOptions: [selectedOrderData?.recipient_customer],
    mapToOption: (recipientCustomer) => ({
      value: recipientCustomer.id,
      label: `${recipientCustomer.id} - ${recipientCustomer.alias}`,
    }),
    setFormData,
  })

  useEffect(() => {
    if (selectedOrderData?.recipient_customer) {
      setFormData((prev) => ({
        ...prev,
        movement: {
          ...prev.movement,
          recipient_customer_id: selectedOrderData?.recipient_customer?.id,
        },
      }))
    }
  }, [selectedOrderData])

  // Objeto selects — usado por getOptions e getSelectedValue
  const selects = {
    'movement.recipient_customer_id': recipientCustomerSelect,
    'movement.service_order_id': ordersServicesSelect,
  }

  // Popular campos informativos da requisição (somente leitura)
  useEffect(() => {
    if (!selectedOrderData) return

    const destination = selectedOrderData?.destination_address
    const itemAddress = serviceOrderItemsData?.[0]?.address
    const address = destination ?? itemAddress

    const addressFormatted = address
      ? [
          address.alias,
          address.street &&
            `${address.street}${address.number ? `, ${address.number}` : ''}`,
          address.city &&
            `${address.city}${address.state ? ` - ${address.state}` : ''}`,
          address.zip && `CEP: ${address.zip}`,
        ]
          .filter(Boolean)
          .join(' - ')
      : '-'

    setFormData((prev) => ({
      ...prev,
      info: {
        movement_type: selectedOrderData?.movement_type?.name ?? '-',
        contract: selectedOrderData?.contract
          ? `${selectedOrderData.contract.number} - ${selectedOrderData.contract.name ?? selectedOrderData.contract.alias ?? ''}`
          : '-',
        destination: destination?.alias ?? itemAddress?.alias ?? '-',
        address: addressFormatted,
        deadline: selectedOrderData?.deadline
          ? dayjs(selectedOrderData.deadline).format('DD/MM/YYYY')
          : '-',
      },
    }))
  }, [selectedOrderData, serviceOrderItemsData])

  const createMovementMutation = useMutation({
    mutationFn: async (dataToCreate) => {
      showLoader()
      setFormErrors({})
      const response = await baseServicePost(
        entities.movements.create,
        dataToCreate
      )
      return response
    },
    onSuccess: (data) => {
      if (data?.status === 201) {
        //  Limpar reservas: movimento foi cadastrado, status agora é gerenciado pelo backend
        reservedEquipments.current.clear()
        latestDraftRef.current = null
        clearMovementDraft(draftKey)
        resetForm()
        setFieldsData({})

        // Invalida ShipmentsPage
        queryClient.invalidateQueries({ queryKey: ['shipments'] })
        // Invalida MovementPage
        queryClient.invalidateQueries({ queryKey: ['movements'] })
        // Invalida CreateMovementPage
        queryClient.invalidateQueries({ queryKey: ['ordersServices'] })
        queryClient.invalidateQueries({ queryKey: ['serviceOrderItems'] })
        queryClient.invalidateQueries({
          queryKey: ['contractItemsForMovement'],
        })
        // Invalida CreateContractOsPage
        queryClient.invalidateQueries({ queryKey: ['contractDetails'] })
        queryClient.invalidateQueries({ queryKey: ['contractItems'] })
        queryClient.invalidateQueries({ queryKey: ['movementItems'] })
        queryClient.invalidateQueries({
          queryKey: ['contractItemsForMovement_CreateMovementItem'],
        })
        // Invalidar DetailsMovementPage
        queryClient.invalidateQueries({ queryKey: ['movementDetails'] })
        queryClient.invalidateQueries({ queryKey: ['osDetails'] })

        navigate(`/aluguel/movimentos`)
      }
    },
    onError: (err) => {
      console.error('onError ', err)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
    },
  })

  const handleSubmit = useCallback(async () => {
    try {
      const assetsArray = Object.values(assets).filter(Boolean)
      const inputsArray = Object.values(inputs).filter(Boolean)

      const allocatedEquipmentIdentifies = new Set()
      const allocatedProductQuantities = {}

      const formattedVolumes = Array.isArray(volumes)
        ? volumes
            .filter((volume) => volume.items && volume.items.length > 0)
            .map((volume) => ({
              name: volume.name,
              items: volume.items
                .map((item) => {
                  if (item.type === 'equipment') {
                    const realAsset = assets[item.identify]
                    if (!realAsset) return null

                    allocatedEquipmentIdentifies.add(item.identify)
                    return {
                      type: 'equipment',
                      equipment_id: realAsset.id,
                      service_order_item_id: realAsset.service_order_item_id,
                    }
                  }

                  const scannedInput = inputsArray.find(
                    (i) => Number(i.product_id) === Number(item.product_id)
                  )
                  const physicalItemId = scannedInput
                    ? scannedInput.id
                    : item.product_item_id

                  allocatedProductQuantities[physicalItemId] =
                    (allocatedProductQuantities[physicalItemId] || 0) +
                    Number(item.quantity)

                  return {
                    type: 'product',
                    product_item_id: physicalItemId,
                    quantity: Number(item.quantity),
                  }
                })
                .filter(Boolean),
            }))
        : []

      const transformedData = {
        movement: {
          ...transformObjectValues(formData.movement),
          organization_id: selectedOrgan?.id,
        },
        items: [
          ...assetsArray.map((item) => ({
            service_order_item_id: item.service_order_item_id,
            movement_type_id: item.movement_type_id,
            alias: item.alias,
            contract_item_id: item.contract_item_id,
            exchange_type_id: item.exchange_type_id,
            equipment_id: item.id,
            is_extra: item.is_extra,
            ...(item.is_extra && { extra_id: item.extra_id }),
          })),
          ...inputsArray.map((input) => ({
            service_order_item_id: input.service_order_item_id,
            movement_type_id: input.movement_type_id,
            product_item_id: input.id,
            is_extra: false,
            product_id: input?.service_order_item?.product_id,
          })),
        ],
        volumes: formattedVolumes,
      }

      const cleanedData = cleanPayload(transformedData)

      if (cleanedData.items.length === 0) {
        showNotification(
          'warning',
          'Não há itens elegíveis para criar o movimento.'
        )
        return
      }

      const hasItemsOutsideVolumes =
        assetsArray.some(
          (asset) => !allocatedEquipmentIdentifies.has(asset.identify)
        ) ||
        inputsArray.some((input) => {
          const allocated = allocatedProductQuantities[input.id] || 0
          return allocated === 0
        })

      if (hasItemsOutsideVolumes) {
        showNotification(
          'warning',
          'Todos os equipamentos e insumos devem estar distribuídos em volumes antes de finalizar o movimento.'
        )
        return
      }

      // 🆕 CORREÇÃO CRÍTICA: Liberar todas as reservas temporárias antes do POST final
      // O backend exige status_id === AVAILABLE (1) para validar o equipamento
      // Mas o reserve-loading mudou para 10 (em carregamento)
      if (pendingReservationsRef.current.size > 0) {
        await Promise.allSettled(
          Array.from(pendingReservationsRef.current.values())
        )
      }

      const idsToRelease = Array.from(reservedEquipments.current)

      if (idsToRelease.length > 0) {
        console.log(' Liberando reservas antes do POST:', idsToRelease)

        await Promise.allSettled(
          idsToRelease.map((equipmentId) =>
            baseServicePost(
              `${entities.equipments.base}/${equipmentId}/release-loading`,
              {},
              { skipNotification: true }
            )
          )
        )

        reservedEquipments.current.clear()
        // Se o POST falhar, o rascunho continua na tela, mas sem reservas
        // pendentes para liberar (os equipamentos já voltaram para AVAILABLE).
        persistDraftRef.current?.()
      }

      console.log('🚀 Payload Enviado:', cleanedData)
      await createMovementMutation.mutateAsync(cleanedData)
    } catch (error) {
      console.error('Erro ao criar movimento:', error)
      showNotification('error', 'Erro ao criar movimento. Tente novamente.')
    }
  }, [
    assets,
    inputs,
    formData.movement,
    volumes,
    createMovementMutation,
    showNotification,
    selectedOrgan,
    baseServicePost,
  ])

  const handleBack = useCallback(async () => {
    await releaseAllReservations({ clearDraft: true })
    navigate(`/aluguel/movimentos/`)
  }, [navigate, releaseAllReservations])

  const hasMovementDraft = Boolean(
    serviceOrderId || Object.keys(assets ?? {}).length ||
    Object.keys(inputs ?? {}).length || volumes.length
  )

  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.options || []
    },
    [selects]
  )

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [selects]
  )

  const filteredOrders = useMemo(() => {
    if (!ordersServicesSelect.rawData) return []
    return ordersServicesSelect.rawData.filter((order) => {
      const deadlineDate = dayjs(order.deadline, 'YYYY-MM-DD')
      const now = dayjs()

      const matchDeadline =
        !filterOptions.deadline ||
        (filterOptions.deadline.value === 'late' &&
          deadlineDate.isBefore(now, 'day')) ||
        (filterOptions.deadline.value === 'onTime' &&
          !deadlineDate.isBefore(now, 'day'))
      const matchRecipientCustomer =
        !filterOptions.recipient_customer ||
        order.recipient_customer?.name ===
          filterOptions.recipient_customer.value
      const matchOrderId =
        !filterOptions.orderId || order.id === filterOptions.orderId.value

      return matchDeadline && matchRecipientCustomer && matchOrderId
    })
  }, [ordersServicesSelect.rawData, filterOptions])

  const checkDeadline = useCallback((deadline) => {
    if (!deadline) return ''
    const deadlineDate = dayjs(deadline, 'YYYY-MM-DD')
    const now = dayjs()
    return deadlineDate.isBefore(now, 'day') ? 'Atrasada' : 'No prazo'
  }, [])

  const addFieldsInData = useCallback(
    async (section) => {
      const key = section.fields[0].id.split('.')[0]
      const newFormErrors = {}
      let hasError = false

      section.fields.forEach((field) => {
        const fieldKey = field.id.split('.')[2]
        const value = fieldsData.items?.[section.index]?.[fieldKey]

        if (
          !field.notRequired &&
          (!value ||
            (typeof value === 'string' && value.trim() === '') ||
            (typeof value === 'object' && !value.value))
        ) {
          hasError = true
          newFormErrors[`${key}.${section.index}.${fieldKey}`] =
            `O campo ${field.label} é obrigatório.`
        }
      })

      if (hasError) {
        setFormErrors((prev) => ({ ...prev, ...newFormErrors }))
        showNotification(
          'error',
          'Por favor, preencha todos os campos obrigatórios.'
        )
        return
      }

      const currentItemBeingAdded = fieldsData.items?.[section.index]
      const existsInItems = formData.items?.some(
        (item) =>
          item.product_id?.value === currentItemBeingAdded?.product_id?.value &&
          item.service_order_item_id?.value ===
            currentItemBeingAdded?.service_order_item_id?.value &&
          item.identify !== currentItemBeingAdded?.identify
      )

      if (existsInItems) {
        showNotification(
          'warning',
          'Este insumo já foi adicionado na lista de itens.'
        )
        return
      }

      setFormData((prev) => {
        const prevItems = Array.isArray(prev.items) ? [...prev.items] : []
        const itemIndexInFormData = prevItems.findIndex(
          (item) => item.identify === currentItemBeingAdded.identify
        )

        if (itemIndexInFormData !== -1) {
          prevItems[itemIndexInFormData] = currentItemBeingAdded
        } else {
          prevItems.push(currentItemBeingAdded)
        }
        return {
          ...prev,
          items: prevItems,
        }
      })

      showNotification(
        'success',
        'Dados do item adicionados/atualizados na lista.'
      )
    },
    [
      fieldsData.items,
      formData.items,
      setFormErrors,
      setFormData,
      showNotification,
    ]
  )
  // 🆕 Libera a reserva do equipamento atualmente no slot (se houver) e limpa o assets
  const releaseAndClearAsset = async (identify) => {
    const current = assets[identify]
    if (!current) return

    setAssets((prev) => {
      const next = { ...prev }
      delete next[identify]
      return next
    })

    if (current.id && reservedEquipments.current.has(current.id)) {
      reservedEquipments.current.delete(current.id)
      try {
        await baseServicePost(
          `${entities.equipments.base}/${current.id}/release-loading`,
          {},
          { skipNotification: true }
        )
      } catch (err) {
        console.error('Erro ao liberar reserva do equipamento anterior:', err)
      }
    }
  }
  const addEquipmentToAssets = async (equipmentNumber, identify) => {
    if (equipmentNumber.trim() === '') return

    showLoader()
    try {
      const movementType = serviceOrderSelected?.movement_type?.id

      const correspondingEquipment = equipmentDataValidated.find(
        (eq) => eq.identify === identify
      )

      const equipmentNumbers = {
        numbers: [equipmentNumber],
        movement_type_id: movementType,
        apply_validation: true,
        compact_response: true,
        recipient_customer_id:
          serviceOrderSelected?.recipient_customer?.id ?? null,
        ...(movementType === MovementEnum.SUBSTITUICAO &&
          correspondingEquipment?.id && {
            equipment_id: correspondingEquipment?.id,
          }),
      }

      const response = await baseServicePost(
        entities.equipments.findByNumber,
        equipmentNumbers,
        { skipNotification: true }
      )

      const equipment = response?.result[0]

      // Equipamento não encontrado
      if (!equipment) {
        setValue(`equipmentsNumber.${identify}`, '')
        await releaseAndClearAsset(identify) // 🆕 libera reserva antiga (se houver) antes de limpar
        setTimeout(() => inputRefs.current[identify]?.focus(), 0)
        return
      }

      if (equipment.is_reserved_for_customer) {
        const confirmed = await askReservedEquipmentConfirmation(
          equipment.number,
          equipment.reserved_for_alias
        )
        if (!confirmed) {
          setValue(`equipmentsNumber.${identify}`, '')
          await releaseAndClearAsset(identify)
          setTimeout(() => inputRefs.current[identify]?.focus(), 0)
          return
        }
      }

      if (equipment.is_reserved_for_another_customer) {
        await askReservedEquipmentConfirmation(
          equipment.number,
          equipment.reserved_for_alias,
          true,
          'reserved'
        )
        setValue(`equipmentsNumber.${identify}`, '')
        await releaseAndClearAsset(identify)
        setTimeout(() => inputRefs.current[identify]?.focus(), 0)
        return
      }

      if (equipment.is_rented_for_another_customer) {
        await askReservedEquipmentConfirmation(
          equipment.number,
          equipment.rented_for_alias,
          true,
          'rented'
        )
        setValue(`equipmentsNumber.${identify}`, '')
        await releaseAndClearAsset(identify)
        setTimeout(() => inputRefs.current[identify]?.focus(), 0)
        return
      }

      // Equipamento em manutenção/carregamento
      if (Number(equipment.status_id) === 10) {
        showNotification(
          'warning',
          `Equipamento ${equipment.number} não está disponível: já está em um carregamento.`
        )
        setValue(`equipmentsNumber.${identify}`, '')
        await releaseAndClearAsset(identify) // 🆕 libera reserva antiga (se houver) antes de limpar
        setTimeout(() => inputRefs.current[identify]?.focus(), 0)
        return
      }

      const strategies = {
        [MovementEnum.SUBSTITUICAO]: () => {
          const osItemId = correspondingEquipment?.service_order_item_id
          return serviceOrderItemsData?.find((item) => item.id === osItemId)
        },
        [MovementEnum.AVULSO]: () => {
          const itemId = correspondingEquipment?.type?.id
          return serviceOrderItemsData?.find((item) => item.type_id === itemId)
        },
        default: () => {
          const itemId =
            correspondingEquipment?.contract_item?.id ??
            correspondingEquipment?.last_relevant_service_order_item?.id

          return serviceOrderItemsData?.find((item) =>
            item.contract_item_id != null
              ? item.contract_item_id === itemId
              : item.id === itemId
          )
        },
      }

      const validOrderItem =
        strategies[movementType]?.() ?? strategies.default()

      if (!validOrderItem) {
        setTimeout(() => inputRefs.current[identify]?.focus(), 0)
        return
      }

      const item = {
        id: equipment.id,
        alias: equipment.name,
        identify: correspondingEquipment.identify,
        service_order_item_id: validOrderItem.id,
        movement_type_id: validOrderItem.movement_type_id,
        ...(correspondingEquipment?.type?.id && {
          exchange_type_id: correspondingEquipment.type.id,
        }),
        ...(correspondingEquipment?.contract_item?.id && {
          contract_item_id: correspondingEquipment.contract_item.id,
        }),
        is_extra: correspondingEquipment.is_extra,
        extra_id: correspondingEquipment?.pivot?.id,
      }

      const detailsEquipment = {
        id: equipment.id,
        name: equipment.name ?? '-',
        number: equipment.number ?? '-',
        serial_number: equipment.serial_number ?? '-',
        status: equipment.status?.name ?? '-',
        purchase_date: equipment.creation_transaction?.purchase_date ?? '-',
        warranty_date:
          equipment.creation_transaction_item?.warranty_date ?? '-',
        current_organization: equipment.organization?.name ?? '-',
        owner_organization: equipment.creation_transaction?.buyer.name ?? '-',
        doc_number: equipment.creation_transaction?.doc_number ?? '-',
        supplier: equipment.creation_transaction?.supplier.name ?? '-',
        category: equipment.creation_transaction_item?.category.name ?? '-',
        condition: equipment.creation_transaction_item?.condition.name ?? '-',
        length: equipment.product?.length ?? '-',
        width: equipment.product?.width ?? '-',
        height: equipment.product?.height ?? '-',
        weight: equipment.product?.weight ?? '-',
      }

      // 🆕 Já existe algo nesse slot? Trata como SUBSTITUIÇÃO em vez de bloquear
      const previousAsset = assets[identify]
      if (previousAsset) {
        // Mesmo equipamento redigitado: não faz nada
        if (String(previousAsset.id) === String(equipment.id)) {
          setTimeout(() => inputRefs.current[identify]?.focus(), 0)
          return
        }
        // Equipamento diferente: libera a reserva do antigo antes de seguir
        if (
          previousAsset.id &&
          reservedEquipments.current.has(previousAsset.id)
        ) {
          reservedEquipments.current.delete(previousAsset.id)
          baseServicePost(
            `${entities.equipments.base}/${previousAsset.id}/release-loading`,
            {},
            { skipNotification: true }
          ).catch((err) =>
            console.error(
              'Erro ao liberar reserva do equipamento anterior:',
              err
            )
          )
        }
      }

      // 🆕 Duplicidade checada em OUTROS slots, ignorando o slot que estamos substituindo
      const existingItem = Object.entries(assets).find(
        ([key, i]) => key !== identify && i && String(i.id) === String(item.id)
      )

      if (existingItem) {
        showNotification('warning', 'Equipamento já consta na lista.')
        setTimeout(() => inputRefs.current[identify]?.focus(), 0)
        return
      }

      setAssets((prevList) => ({
        ...prevList,
        [identify]: {
          ...item,
          equipment: detailsEquipment,
          isNew: true,
        },
      }))

      const allIdentifies = equipmentDataValidated.map((e) => e.identify)
      const currentIndex = allIdentifies.indexOf(identify)
      const nextIdentify = allIdentifies[currentIndex + 1]

      if (nextIdentify && inputRefs.current[nextIdentify]) {
        setTimeout(() => inputRefs.current[nextIdentify]?.focus(), 0)
      }

      if (
        Number(equipment.status_id) === 1 &&
        !reservedEquipments.current.has(equipment.id)
      ) {
        reservedEquipments.current.add(equipment.id)
        const reservationRequest = baseServicePost(
          `${entities.equipments.base}/${equipment.id}/reserve-loading`,
          {},
          { skipNotification: true }
        )
          .catch((error) => {
            console.error('Erro ao reservar equipamento:', error)
            reservedEquipments.current.delete(equipment.id)
            showNotification(
              'warning',
              `Equipamento ${equipment.number} adicionado, mas reserva falhou.`
            )
          })
          .finally(() => {
            pendingReservationsRef.current.delete(equipment.id)
          })

        pendingReservationsRef.current.set(equipment.id, reservationRequest)
      }
    } catch (error) {
      setTimeout(() => inputRefs.current[identify]?.focus(), 0)
      console.error('Erro ao buscar insumo:', error)
    } finally {
      hideLoader()
    }
  }

  const {
    debouncedFn: debouncedAddEquipment,
    flush: flushAddEquipment,
    isPending: isDebounceEquipmentPending,
  } = useDebounce(addEquipmentToAssets, 100)

  const addProductToInputs = async (code, identify) => {
    if (code.trim() === '') return
    showLoader()
    try {
      const correspondingProduct = products.find(
        (eq) => eq.identify === identify
      )

      const payload = {
        codes: [code],
        service_order_id: serviceOrderId,
      }

      const response = await baseServicePost(
        entities.products.getByCode,
        payload,
        { skipNotification: true }
      )

      const productItem =
        response?.result && response.result.length > 0
          ? response.result[0]
          : null

      if (!productItem) {
        setTimeout(() => inputRefs.current[identify]?.focus(), 0)
        return
      }

      const validOrderItem = serviceOrderItemsData?.find(
        (oSItem) => correspondingProduct.product_id === oSItem?.product?.id
      )

      if (!validOrderItem) {
        setTimeout(() => inputRefs.current[identify]?.focus(), 0)
        return
      }

      const item = {
        id: productItem?.id,
        product_id: productItem?.product_id,
        alias: productItem?.product?.name ?? '-',
        code: productItem?.code ?? '-',
        identify: correspondingProduct?.identify,
        service_order_item_id: validOrderItem.id,
        service_order_item: validOrderItem,
        movement_type_id: validOrderItem.movement_type_id,
      }

      const detailsProduct = {
        id: productItem?.id,
        code: productItem.code ?? '-',
        name: productItem?.product?.name ?? '-',
        quantity:
          correspondingProduct?.product_items?.find(
            (groupedItem) => groupedItem.code === productItem.code
          )?.quantity ??
          productItem?.quantity ??
          '-',
        is_asset: productItem?.product?.is_asset,
        length: productItem?.product?.length ?? '-',
        organization: productItem?.organization?.name ?? '-',
        width: productItem?.product?.width ?? '-',
        height: productItem?.product?.height ?? '-',
        weight: productItem?.product?.weight ?? '-',
      }

      // 🆕 Já existe algo nesse slot? Trata como SUBSTITUIÇÃO
      const previousInput = inputs[identify]
      if (previousInput && String(previousInput.id) === String(item.id)) {
        // Mesmo insumo redigitado: não faz nada
        setTimeout(() => inputRefs.current[identify]?.focus(), 0)
        return
      }

      setInputs((prevList) => {
        const existingItem = Object.entries(prevList).find(
          ([key, i]) =>
            key !== identify && i && String(i.id) === String(item.id)
        )

        if (existingItem) {
          showNotification('warning', 'Insumo já consta na lista.')
          return prevList
        }

        const updated = {
          ...prevList,
          [identify]: {
            ...item,
            product: {
              ...detailsProduct,
              is_asset: productItem?.product?.is_asset ? 'Sim' : 'Não',
            },
            isNew: true,
          },
        }

        const allIdentifies = products.map((e) => e.identify)
        const currentIndex = allIdentifies.indexOf(identify)
        const nextIdentify = allIdentifies[currentIndex + 1]

        if (nextIdentify && inputRefs.current[nextIdentify]) {
          setTimeout(() => {
            inputRefs.current[nextIdentify]?.focus()
          }, 0)
        }

        return updated
      })

      // 🆕 Se estava substituindo um insumo diferente, remove o antigo dos volumes
      // e devolve a quantidade que ele ocupava para o saldo restante
      if (previousInput && String(previousInput.id) !== String(item.id)) {
        const oldProductItemId = previousInput.id
        const oldProductId = previousInput.product_id

        const totalRemoved = volumes.reduce((acc, volume) => {
          const volumeQty = volume.items
            ?.filter((it) => it.product_item_id === oldProductItemId)
            .reduce((sum, it) => sum + (Number(it.quantity) || 0), 0)
          return acc + (volumeQty || 0)
        }, 0)

        setVolumes((prev) =>
          prev.map((v) => ({
            ...v,
            items: Array.isArray(v.items)
              ? v.items.filter((it) => it.product_item_id !== oldProductItemId)
              : [],
          }))
        )

        if (totalRemoved > 0) {
          setProducts((prev) =>
            prev.map((p) =>
              p.product_id === oldProductId
                ? { ...p, quantity: (Number(p.quantity) || 0) + totalRemoved }
                : p
            )
          )
        }
      }
    } catch (error) {
      setTimeout(() => inputRefs.current[identify]?.focus(), 0)
      console.error('Erro ao buscar insumo:', error)
    } finally {
      hideLoader()
    }
  }

  const { debouncedFn: debouncedAddProduct } = useDebounce(
    addProductToInputs,
    300
  )

  const handleRemoveItem = useCallback(
    (volumeIndex, itemIndex, volume) => {
      setVolumes((prevVolumes) => {
        const updatedVolumes = [...prevVolumes]
        const targetVolume = { ...updatedVolumes[volumeIndex] }

        // Garante que se items for null/undefined, vira um array vazio e evita o quebra-código
        const targetItems = Array.isArray(targetVolume.items)
          ? [...targetVolume.items]
          : []
        const itemToRemove = targetItems[itemIndex]

        if (!itemToRemove) return prevVolumes

        // 1. Se for EQUIPAMENTO: Remove direto (quantidade é sempre 1)
        if (itemToRemove.type === 'equipment') {
          targetItems.splice(itemIndex, 1)
          showNotification('success', 'Equipamento removido do volume.')
        }
        // 2. Se for PRODUTO / INSUMO:
        else if (itemToRemove.type === 'product') {
          const currentQty = itemToRemove.quantity

          if (currentQty > 1) {
            // O prompt avisa que se der OK sem mudar, remove tudo.
            const promptQty = prompt(
              `Quantas unidades de "${itemToRemove.product_name}" deseja remover?\n\n` +
                `• Digite um valor menor para remover parcialmente.\n` +
                `• Clique em OK (com o valor ${currentQty}) para remover TODO o insumo do volume.`,
              currentQty
            )

            // Se o usuário clicou em "Cancelar", interrompe completamente e não faz nada
            if (promptQty === null) return prevVolumes

            const qtyToRemove = Number(promptQty)

            // Validação se o cara digitou alguma letra ou valor bizarro
            if (isNaN(qtyToRemove) || qtyToRemove <= 0) {
              showNotification('warning', 'Quantidade inválida inserida.')
              return prevVolumes
            }

            // Se a quantidade digitada (ou mantida) for igual ou maior que a atual, remove TUDO
            if (qtyToRemove >= currentQty) {
              targetItems.splice(itemIndex, 1)
              showNotification(
                'success',
                'Insumo removido completamente do volume.'
              )
            } else {
              // Subtrai cirurgicamente apenas o valor que ele digitou
              targetItems[itemIndex] = {
                ...itemToRemove,
                quantity: currentQty - qtyToRemove,
              }
              showNotification(
                'success',
                `${qtyToRemove} unidades removidas. Restaram ${currentQty - qtyToRemove} no volume.`
              )
            }
          } else {
            // Se a quantidade já era 1, remove direto sem abrir prompt
            targetItems.splice(itemIndex, 1)
            showNotification('success', 'Insumo removido do volume.')
          }
        }

        targetVolume.items = targetItems
        updatedVolumes[volumeIndex] = targetVolume
        return updatedVolumes
      })
    },
    [showNotification]
  )

  const handleRemoveBipedItem = useCallback(
    (identifyToRemove) => {
      if (!identifyToRemove) return

      const productItemId = inputs[identifyToRemove]?.id
      const productId = inputs[identifyToRemove]?.product_id
      const equipmentId = assets[identifyToRemove]?.id // 👈 Captura o ID do equipamento

      // SE FOR PRODUTO / INSUMO
      if (productItemId) {
        const totalRemoved = volumes.reduce((acc, volume) => {
          const volumeQty = volume.items
            ?.filter((item) => item.product_item_id === productItemId)
            .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
          return acc + (volumeQty || 0)
        }, 0)

        setInputs((prev) => {
          const next = { ...prev }
          delete next[identifyToRemove]
          return next
        })

        setVolumes((prev) =>
          prev.map((v) => ({
            ...v,
            items: Array.isArray(v.items)
              ? v.items.filter((item) => item.product_item_id !== productItemId)
              : [],
          }))
        )

        if (totalRemoved > 0) {
          setProducts((prev) =>
            prev.map((p) =>
              p.product_id === productId
                ? { ...p, quantity: (Number(p.quantity) || 0) + totalRemoved }
                : p
            )
          )
        }
      }
      // SE FOR EQUIPAMENTO / PATRIMÔNIO
      else {
        // 1. Remove do estado global de assets
        setAssets((prev) => {
          const next = { ...prev }
          delete next[identifyToRemove]
          return next
        })

        // 2. 🛡️ BLINDAGEM: Só libera a reserva no backend se o equipamento foi EFETIVAMENTE adicionado antes
        if (equipmentId) {
          baseServicePost(
            `${entities.equipments.base}/${equipmentId}/release-loading`,
            {},
            { skipNotification: true }
          ).catch((err) => console.error('Erro ao liberar reserva:', err))

          reservedEquipments.current.delete(equipmentId)
        }

        // 3. Limpa o equipamento de dentro de qualquer volume
        setVolumes((prev) =>
          prev.map((v) => ({
            ...v,
            items: Array.isArray(v.items)
              ? v.items.filter((item) => item.identify !== identifyToRemove)
              : [],
          }))
        )
      }

      // 4. Reseta os campos no React Hook Form (garante que o input fique visualmente vazio)
      setValue(`equipmentsNumber.${identifyToRemove}`, '')
      setValue(`code.${identifyToRemove}`, '')

      // 5. Devolve o foco para o input
      setTimeout(() => {
        if (inputRefs.current?.[identifyToRemove]) {
          inputRefs.current[identifyToRemove].focus()
        } else if (firstInputRef?.current) {
          firstInputRef.current.focus()
        }
      }, 50)
    },
    [
      inputs,
      volumes,
      assets,
      setValue,
      setProducts,
      setInputs,
      setVolumes,
      setAssets,
      inputRefs,
      firstInputRef,
    ]
  )

  const hasInitialFocused = useRef(false)

  useEffect(() => {
    if (
      formData.movement.service_order_id &&
      equipmentDataValidated &&
      equipmentDataValidated.length > 0 &&
      !hasInitialFocused.current // só foca na primeira vez
    ) {
      const timer = setTimeout(() => {
        if (firstInputRef.current) {
          firstInputRef.current.focus()
          hasInitialFocused.current = true // marca que já focou
        }
      }, 0)

      return () => clearTimeout(timer)
    }
  }, [formData.movement.service_order_id, equipmentDataValidated])

  // Saída da tela:
  // - reload/fechamento (beforeunload/pagehide): preserva rascunho e reservas;
  // - navegação normal (Voltar/menu): libera reservas e descarta o rascunho.
  useEffect(() => {
    const handlePrepareLogout = () => {
      // Captura o estado React mais recente de forma síncrona antes do logout
      // remover token e demais dados locais.
      persistDraftRef.current?.()
    }

    const handleBeforeUnload = () => {
      persistDraftRef.current?.()
    }

    const handlePageHide = () => {
      isPageUnloadingRef.current = true
      persistDraftRef.current?.()
    }

    // Retorno do bfcache (voltar/avançar do navegador): a página continua
    // viva e uma navegação SPA posterior deve voltar a liberar as reservas.
    const handlePageShow = () => {
      isPageUnloadingRef.current = false
    }

    window.addEventListener('movement:create:prepare-logout', handlePrepareLogout)
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('pagehide', handlePageHide)
    window.addEventListener('pageshow', handlePageShow)

    return () => {
      window.removeEventListener('movement:create:prepare-logout', handlePrepareLogout)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('pagehide', handlePageHide)
      window.removeEventListener('pageshow', handlePageShow)

      // A sessão expirada redireciona para o login sem ser uma saída voluntária:
      // preserva o rascunho e as reservas para oferecer retomada após autenticar.
      if (wasMovementSessionExpired()) {
        persistDraftRef.current?.()
        return
      }

      // Reload/fechamento: mantém o rascunho e as reservas para retomar.
      if (isPageUnloadingRef.current && draftKey) return

      releaseAllRef.current?.({ clearDraft: true })
    }
  }, [])

  return {
    formData,
    handleChange,
    handleSubmit,
    handleBack,
    hasMovementDraft,
    formErrors,
    createMovementMutation,
    viewMode,
    setViewMode,
    filteredOrders,
    filterOptions,
    setFilterOptions,
    checkDeadline,
    addFieldsInData,
    viewTable,
    setViewTable,
    getOptions,
    getSelectedValue,
    formFields: movementFields,
    equipments: equipmentDataValidated,
    products,
    setProducts,
    volumes,
    setVolumes,
    assets,
    inputs,
    setInputs,
    setAssets,
    control,
    setValue,
    isLoading: useLoader().isLoading || isLoadingEquipments,
    isDebouncePending: isDebounceEquipmentPending,
    debouncedAddEquipment,
    flushAddEquipment,
    handleRemoveItem,
    handleRemoveBipedItem,
    debouncedAddProduct,
    reservedConfirmState,
    handleReservedConfirm,
    handleReservedCancel,
    firstInputRef,
    inputRefs,
    serviceOrderItemsData,
  }
}

export default useCreateMovement
